import { useEffect, useRef } from 'react';
import { AssistantTouchedResource } from '../interfaces/Assistant';

/**
 * Magistrală de live-sync pentru schimbările produse de TMS Copilot.
 *
 * Când asistentul creează/modifică/șterge ceva, backendul întoarce `touchedResources`
 * (kind + id + op). `publishCopilotChange` difuzează aceste schimbări către toate
 * paginile deschise (din același tab ȘI din alte tab-uri), iar componentele care
 * monitorizează un anumit `kind` (prin `useCopilotResourceSync`) reacționează: își
 * reîmprospătează datele și animează rândul/câmpul atins (shimmer).
 *
 * Transport: `BroadcastChannel` (cross-tab) + `window.CustomEvent` (same-tab, pentru că
 * BroadcastChannel nu trimite mesajul înapoi la tab-ul emitent). Fără WebSocket — e un
 * pattern de tip „best-effort refetch", identic cu cel din Ahead.
 */

const CHANNEL_NAME = 'tms-copilot-sync';
const WINDOW_EVENT_NAME = 'tms-copilot-sync';

interface BusMessage {
  type: 'resources-changed';
  resources: AssistantTouchedResource[];
  ts: number;
}

let channel: BroadcastChannel | null = null;
function getChannel(): BroadcastChannel | null {
  if (typeof window === 'undefined') return null;
  if (channel) return channel;
  try {
    channel = new BroadcastChannel(CHANNEL_NAME);
    return channel;
  } catch {
    // BroadcastChannel poate lipsi în medii vechi / de test — degradăm grațios la same-tab.
    return null;
  }
}

/** Difuzează un set de resurse atinse către paginile deschise (same-tab + cross-tab). */
export function publishCopilotChange(resources: AssistantTouchedResource[] | null | undefined): void {
  if (!resources || resources.length === 0) return;
  const msg: BusMessage = { type: 'resources-changed', resources, ts: Date.now() };

  // Same-tab (BroadcastChannel sare peste tab-ul emitent).
  try {
    window.dispatchEvent(new CustomEvent(WINDOW_EVENT_NAME, { detail: msg }));
  } catch {
    /* ignore */
  }

  // Cross-tab.
  const ch = getChannel();
  if (ch) {
    try {
      ch.postMessage(msg);
    } catch {
      /* ignore */
    }
  }
}

export interface UseCopilotResourceSyncOptions {
  /** Dacă e setat, reacționează doar la resursele cu acest id. */
  id?: number | string | null;
  /** Dacă e setat, reacționează doar la aceste operații (ex: ['update','delete']). */
  ops?: AssistantTouchedResource['op'][];
  /** Debounce pentru a evita refetch-uri repetate când un tool atinge mai multe resurse. */
  debounceMs?: number;
  /** Apelat (debounced) cu ultima resursă relevantă. */
  onChange: (resource: AssistantTouchedResource) => void;
}

/**
 * Abonează o componentă la schimbările unui anumit `kind` de resursă.
 * Filtrează după `kind` (obligatoriu), opțional `id` și `ops`, cu debounce.
 */
export function useCopilotResourceSync(kind: string, opts: UseCopilotResourceSyncOptions): void {
  const optsRef = useRef(opts);
  optsRef.current = opts;

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    let pending: AssistantTouchedResource | null = null;

    const handle = (resources: AssistantTouchedResource[]) => {
      const { id, ops, debounceMs = 150 } = optsRef.current;
      const idStr = id != null ? String(id) : undefined;

      for (const r of resources) {
        if (!r || r.kind !== kind) continue;
        if (idStr !== undefined && r.id != null && String(r.id) !== idStr) continue;
        if (ops && !ops.includes(r.op)) continue;
        pending = { ...r, id: r.id != null ? String(r.id) : r.id };
      }

      if (!pending) return;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        const toFire = pending;
        pending = null;
        timer = null;
        if (toFire) optsRef.current.onChange(toFire);
      }, debounceMs);
    };

    const onWindow = (ev: Event) => {
      const detail = (ev as CustomEvent<BusMessage>).detail;
      if (detail?.type === 'resources-changed') handle(detail.resources);
    };
    const onChannel = (ev: MessageEvent<BusMessage>) => {
      if (ev.data?.type === 'resources-changed') handle(ev.data.resources);
    };

    window.addEventListener(WINDOW_EVENT_NAME, onWindow);
    const ch = getChannel();
    if (ch) ch.addEventListener('message', onChannel);

    return () => {
      window.removeEventListener(WINDOW_EVENT_NAME, onWindow);
      if (ch) ch.removeEventListener('message', onChannel);
      if (timer) clearTimeout(timer);
    };
  }, [kind]);
}
